'use client'

import { Box, Button, Input, InputGroup, Stack, Text } from '@chakra-ui/react'
import { Link } from '@tanstack/react-router'
import { Eye, EyeOff, Lock, Mail } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { AppField } from '@/shared'
import { useLoginForm } from '../model/use-login-form'

export function LoginForm() {
  const { t } = useTranslation()
  const { form, onSubmit } = useLoginForm()
  const [showPassword, setShowPassword] = useState(false)

  const {
    register,
    formState: { errors, isSubmitting },
  } = form

  return (
    <form noValidate onSubmit={onSubmit}>
      <Stack gap={4}>
        <AppField.Root invalid={!!errors.email}>
          <AppField.Label fontSize="sm" color="fg.muted">
            {t('auth.login.email')}
          </AppField.Label>
          <InputGroup startElement={<Mail size={16} />}>
            <Input
              {...register('email')}
              type="email"
              placeholder={t('auth.login.emailPlaceholder')}
              size="md"
              bg="bg.surface"
              borderColor={errors.email ? 'primary.fg' : 'border'}
              borderRadius="lg"
            />
          </InputGroup>
          <AppField.ErrorText>{errors.email?.message}</AppField.ErrorText>
        </AppField.Root>

        <AppField.Root invalid={!!errors.password}>
          <AppField.Label fontSize="sm" color="fg.muted">
            {t('auth.login.password')}
          </AppField.Label>
          <InputGroup
            startElement={<Lock size={16} />}
            endElement={
              <Button
                type="button"
                variant="ghost"
                size="xs"
                minW="auto"
                px={1}
                color="fg.muted"
                onClick={() => setShowPassword((current) => !current)}
                aria-label={
                  showPassword
                    ? t('auth.login.hidePassword')
                    : t('auth.login.showPassword')
                }
              >
                {showPassword ? <EyeOff size={15} /> : <Eye size={15} />}
              </Button>
            }
          >
            <Input
              {...register('password')}
              type={showPassword ? 'text' : 'password'}
              placeholder={t('auth.login.passwordPlaceholder')}
              size="md"
              bg="bg.surface"
              borderColor={errors.password ? 'primary.fg' : 'border'}
              borderRadius="lg"
            />
          </InputGroup>
          <AppField.ErrorText>{errors.password?.message}</AppField.ErrorText>
        </AppField.Root>

        <Button
          mt={2}
          type="submit"
          size="md"
          bg="primary.solid"
          color="white"
          borderRadius="lg"
          _hover={{ opacity: 0.88 }}
          _active={{ opacity: 0.72 }}
          loading={isSubmitting}
          loadingText={t('auth.login.signingIn')}
        >
          {t('auth.login.signIn')}
        </Button>

        <Text fontSize="sm" color="fg.muted" textAlign="center">
          {t('auth.login.newHere')}{' '}
          <Box as="span" color="primary.fg" fontWeight={600}>
            <Link to="/auth/register" style={{ color: 'inherit' }}>
              {t('auth.login.createAccount')}
            </Link>
          </Box>
        </Text>
      </Stack>
    </form>
  )
}
